/**
 * AgentsModule — Pantalla de gestión de agentes on-premise.
 *
 * Layout: panel izquierdo (lista de agentes) + panel derecho (detalle del agente)
 * - Estado en tiempo real con auto-refresh cada 15s
 * - Renombrar y revocar agentes
 * - Historial de ejecuciones recientes del agente
 * - Capacidades: browsers, paralelismo
 *
 * FASE 1: Solo gestión de agentes.
 * FASE 2: Vinculación con planes de ejecución (preferred_agent_id).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { agentsApi } from '../../services/api';
import type { AgentRunSummary } from '../../services/api';
import type { Agent } from '../../types';

// ─── Status config ─────────────────────────────────────────────────────────

const STATUS_CFG: Record<string, { color: string; bg: string; label: string; dot: string }> = {
  online:  { color: '#0F6E56', bg: '#E1F5EE', label: 'Online',       dot: '#1D9E75' },
  busy:    { color: '#185FA5', bg: '#E6F1FB', label: 'Ejecutando',   dot: '#3B8BD4' },
  offline: { color: '#5F5E5A', bg: '#F1EFE8', label: 'Offline',      dot: '#C0BEB9' },
  error:   { color: '#A32D2D', bg: '#FCEBEB', label: 'Error',        dot: '#E24B4A' },
};

const PLATFORM_CFG: Record<string, { icon: string; label: string }> = {
  web:    { icon: '🌐', label: 'Web' },
  mobile: { icon: '📱', label: 'Mobile' },
  api:    { icon: '⚡', label: 'API' },
};

// ─── Helpers ───────────────────────────────────────────────────────────────

// ─── Status resolution (fuente única de verdad) ────────────────────────────
// Un agente cuyo último heartbeat supera STALE_MS se trata como offline
// independientemente del valor almacenado en BD.

const STALE_MS = 120_000; // 2 minutos

function resolveStatus(agent: { status: string; last_heartbeat?: string | null }): string {
  const stale = agent.last_heartbeat
    ? Date.now() - new Date(agent.last_heartbeat).getTime() > STALE_MS
    : true;
  // Un agente busy nunca se marca offline por stale: está en medio de un run
  return stale && agent.status !== 'busy' ? 'offline' : agent.status;
}

function timeAgo(iso?: string) {
  if (!iso) return 'nunca';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60000) return `hace ${Math.round(diff / 1000)}s`;
  if (diff < 3600000) return `hace ${Math.round(diff / 60000)}m`;
  if (diff < 86400000) return `hace ${Math.round(diff / 3600000)}h`;
  return `hace ${Math.round(diff / 86400000)}d`;
}

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' });
}

function Spinner() {
  return <div style={{ padding: 30, textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 12 }}>Cargando...</div>;
}

function EmptyState({ icon, text, sub }: { icon: string; text: string; sub?: string }) {
  return (
    <div style={{ padding: '48px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: 'var(--color-text-tertiary)', textAlign: 'center' }}>
      <span style={{ fontSize: 36 }}>{icon}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-secondary)' }}>{text}</span>
      {sub && <span style={{ fontSize: 12 }}>{sub}</span>}
    </div>
  );
}

// ─── Status Dot ────────────────────────────────────────────────────────────

function StatusDot({ status, size = 8 }: { status: string; size?: number }) {
  const cfg = STATUS_CFG[status] ?? STATUS_CFG.offline;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: cfg.dot, flexShrink: 0,
      boxShadow: status === 'busy' ? `0 0 0 3px ${cfg.dot}33` : status === 'online' ? `0 0 0 2px ${cfg.dot}44` : 'none',
      animation: status === 'busy' ? 'pulse 1.5s ease-in-out infinite' : 'none',
    }} />
  );
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CFG[status] ?? STATUS_CFG.offline;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px', borderRadius: 6, background: cfg.bg, color: cfg.color, fontSize: 10, fontWeight: 700 }}>
      <StatusDot status={status} size={6} />
      {cfg.label}
    </span>
  );
}

// ─── Rename Modal ───────────────────────────────────────────────────────────

function RenameModal({ agent, onSave, onClose }: {
  agent: Agent; onSave: (name: string) => Promise<void>; onClose: () => void;
}) {
  const [name, setName] = useState(agent.name);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || name === agent.name) { onClose(); return; }
    setSaving(true);
    await onSave(name.trim());
    setSaving(false);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ width: 380, borderRadius: 14, background: 'var(--color-background-primary)', border: '1px solid var(--color-border-secondary)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)' }}>Renombrar agente</span>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>✕</button>
        </div>
        <div style={{ padding: 20 }}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 11, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Nombre del agente
          </label>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', fontSize: 13, boxSizing: 'border-box', outline: 'none' }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-secondary)' }}>
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#3B8BD4', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Agent List Item ────────────────────────────────────────────────────────

function AgentItem({ agent, selected, onClick }: {
  agent: Agent; selected: boolean; onClick: () => void;
}) {
  const effectiveStatus = resolveStatus(agent);
  const cfg = STATUS_CFG[effectiveStatus] ?? STATUS_CFG.offline;
  const plat = PLATFORM_CFG[agent.platform] ?? PLATFORM_CFG.web;

  return (
    <div onClick={onClick} style={{
      padding: '12px 14px', borderRadius: 10, cursor: 'pointer', marginBottom: 6,
      border: `1px solid ${selected ? '#3B8BD4' : 'var(--color-border-tertiary)'}`,
      background: selected ? '#E6F1FB22' : 'var(--color-background-primary)',
      transition: 'all 0.15s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <StatusDot status={effectiveStatus} size={10} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {agent.name}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 3, fontSize: 11, color: 'var(--color-text-tertiary)', alignItems: 'center' }}>
            <span>{plat.icon} {plat.label}</span>
            {agent.os_info && <span>· {agent.os_info.split(' ')[0]}</span>}
            <span>· {timeAgo(agent.last_heartbeat)}</span>
          </div>
        </div>
        <StatusBadge status={effectiveStatus} />
      </div>
      {effectiveStatus === 'busy' && (
        <div style={{ marginTop: 8, height: 3, borderRadius: 2, background: 'var(--color-border-tertiary)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: '60%', background: '#3B8BD4', animation: 'slideProgress 2s ease-in-out infinite' }} />
        </div>
      )}
    </div>
  );
}

// ─── Agent Detail Panel ─────────────────────────────────────────────────────

function AgentDetailPanel({ agent, onRename, onRevoke, onClose }: {
  agent: Agent;
  onRename: (name: string) => Promise<void>;
  onRevoke: () => Promise<void>;
  onClose: () => void;
}) {
  const [showRename, setShowRename] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [recentRuns, setRecentRuns] = useState<AgentRunSummary[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);

  const effectiveStatus = resolveStatus(agent);
  const cfg = STATUS_CFG[effectiveStatus] ?? STATUS_CFG.offline;
  const plat = PLATFORM_CFG[agent.platform] ?? PLATFORM_CFG.web;
  const browsers = agent.capabilities?.browsers ?? [];
  const maxParallel = agent.capabilities?.max_parallel ?? 1;

  useEffect(() => {
    setLoadingRuns(true);
    agentsApi.getRuns(agent.id).then(runs => {
      setRecentRuns(runs);
      setLoadingRuns(false);
    });
  }, [agent.id]);

  const [showRevokeModal, setShowRevokeModal] = useState(false);

  const handleRevoke = async () => {
    setRevoking(true);
    await onRevoke();
    setRevoking(false);
    setShowRevokeModal(false);
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        {/* Platform icon */}
        <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--color-background-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
          {plat.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 4 }}>{agent.name}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <StatusBadge status={effectiveStatus} />
            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>v{agent.version}</span>
            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>· {plat.label}</span>
          </div>
        </div>
        <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-tertiary)', flexShrink: 0 }}>✕</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Info grid */}
        <div style={{ borderRadius: 12, border: '1px solid var(--color-border-tertiary)', overflow: 'hidden' }}>
          {[
            { label: 'Estado',        value: <StatusBadge status={effectiveStatus} /> },
            { label: 'Plataforma',    value: `${plat.icon} ${plat.label}` },
            { label: 'S.O. / Arch',   value: agent.os_info ?? '—' },
            { label: 'Versión',       value: `v${agent.version}` },
            { label: 'IP',            value: agent.ip_address ?? '—' },
            { label: 'Último HB',     value: timeAgo(agent.last_heartbeat) },
            { label: 'Registrado',    value: formatDate(agent.registered_at) },
          ].map((row, i) => (
            <div key={row.label} style={{
              display: 'flex', alignItems: 'center', padding: '10px 14px',
              background: i % 2 === 0 ? 'var(--color-background-secondary)' : 'var(--color-background-primary)',
              borderBottom: i < 6 ? '1px solid var(--color-border-tertiary)' : 'none',
            }}>
              <span style={{ width: 130, fontSize: 12, fontWeight: 600, color: 'var(--color-text-tertiary)' }}>{row.label}</span>
              <span style={{ fontSize: 12, color: 'var(--color-text-primary)' }}>
                {typeof row.value === 'string' ? row.value : row.value}
              </span>
            </div>
          ))}
        </div>

        {/* Capabilities */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Capacidades
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Browsers */}
            <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)' }}>
              <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 8, fontWeight: 600 }}>BROWSERS DISPONIBLES</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {browsers.length > 0 ? browsers.map(b => (
                  <span key={b} style={{ padding: '3px 10px', borderRadius: 6, background: 'var(--color-background-primary)', border: '1px solid var(--color-border-secondary)', fontSize: 12, color: 'var(--color-text-primary)', fontWeight: 500 }}>
                    {b === 'chromium' ? '🔵' : b === 'firefox' ? '🦊' : b === 'webkit' ? '🍎' : '🌐'} {b}
                  </span>
                )) : <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>Sin información</span>}
              </div>
            </div>
            {/* Parallelism */}
            <div style={{ padding: '12px 14px', borderRadius: 10, background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', fontWeight: 600 }}>EJECUCIONES PARALELAS</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>Máximo simultáneo</div>
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text-primary)' }}>{maxParallel}</div>
            </div>
          </div>
        </div>

        {/* Current execution */}
        {effectiveStatus === 'busy' && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Ejecución activa
            </div>
            <div style={{ padding: '14px', borderRadius: 10, background: '#E6F1FB', border: '1px solid #B5D4F4' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3B8BD4', animation: 'pulse 1.5s ease-in-out infinite' }} />
                <span style={{ fontSize: 13, fontWeight: 600, color: '#185FA5' }}>Ejecutando casos de prueba</span>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: '#B5D4F4', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: '60%', background: '#3B8BD4', animation: 'slideProgress 2s ease-in-out infinite' }} />
              </div>
              <div style={{ fontSize: 11, color: '#185FA5', marginTop: 6 }}>
                ID de run: {agent.current_run_id?.slice(0, 8)}...
              </div>
            </div>
          </div>
        )}

        {/* Recent runs — evento de entrega de tareas S1→S2 */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Historial de ejecuciones
          </div>
          {loadingRuns ? (
            <Spinner />
          ) : recentRuns.length === 0 ? (
            <EmptyState icon="⏳" text="Sin ejecuciones registradas" sub="Las tareas enviadas por el Sistema 1 aparecerán aquí." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {recentRuns.map(run => {
                const RUN_STATUS_CFG: Record<string, { color: string; bg: string; label: string }> = {
                  passed:  { color: '#0F6E56', bg: '#E1F5EE', label: 'Pasó' },
                  failed:  { color: '#A32D2D', bg: '#FCEBEB', label: 'Falló' },
                  running: { color: '#185FA5', bg: '#E6F1FB', label: 'Ejecutando' },
                  queued:  { color: '#6B4F12', bg: '#FEF3D7', label: 'En cola' },
                  error:   { color: '#A32D2D', bg: '#FCEBEB', label: 'Error' },
                  skipped: { color: '#5F5E5A', bg: '#F1EFE8', label: 'Omitido' },
                  pending: { color: '#5F5E5A', bg: '#F1EFE8', label: 'Pendiente' },
                };
                const rCfg = RUN_STATUS_CFG[run.status] ?? RUN_STATUS_CFG.pending;
                const dur = run.duration_ms != null
                  ? run.duration_ms < 60000
                    ? `${(run.duration_ms / 1000).toFixed(1)}s`
                    : `${Math.round(run.duration_ms / 60000)}m`
                  : null;
                const startedAt = run.started_at ? timeAgo(run.started_at) : (run.finished_at ? timeAgo(run.finished_at) : '—');
                return (
                  <div key={run.run_id} style={{
                    padding: '10px 12px', borderRadius: 8,
                    border: '1px solid var(--color-border-tertiary)',
                    background: 'var(--color-background-secondary)',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <span style={{
                      padding: '2px 7px', borderRadius: 5, fontSize: 10, fontWeight: 700,
                      background: rCfg.bg, color: rCfg.color, flexShrink: 0,
                    }}>{rCfg.label}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {run.plan_name ?? `Plan ${run.plan_id.slice(0, 8)}`}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>
                        {run.passed_steps}/{run.total_steps} pasos · {startedAt}
                        {dur && ` · ${dur}`}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Setup instructions */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
            Instrucciones de conexión
          </div>
          <div style={{ padding: '14px', borderRadius: 10, background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)', fontSize: 12 }}>
            <div style={{ color: 'var(--color-text-secondary)', marginBottom: 8 }}>
              El agente Playwright se conecta al backend mediante pull-polling:
            </div>
            <code style={{ display: 'block', background: 'var(--color-background-primary)', padding: '10px 12px', borderRadius: 8, fontSize: 11, color: '#3B8BD4', lineHeight: 1.6, whiteSpace: 'pre' }}>
{`# Arrancar el agente
docker compose --profile agent up -d

# O manualmente:
QA_AGENT_ID=${agent.id.slice(0, 8)}...
QA_BACKEND_URL=http://localhost:8000`}
            </code>
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div style={{ padding: '14px 22px', borderTop: '1px solid var(--color-border-tertiary)', display: 'flex', gap: 10, justifyContent: 'flex-end', background: 'var(--color-background-primary)' }}>
        <button onClick={() => setShowRename(true)}
          style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 13, color: 'var(--color-text-secondary)', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
          ✎ Renombrar
        </button>
        <button onClick={() => setShowRevokeModal(true)} disabled={revoking}
          style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #F7C1C1', background: revoking ? '#F1EFE8' : 'transparent', cursor: revoking ? 'not-allowed' : 'pointer', fontSize: 13, color: '#A32D2D', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
          {revoking ? '⟳ Revocando...' : '✕ Revocar agente'}
        </button>
      </div>

      {showRename && (
        <RenameModal agent={agent} onSave={onRename} onClose={() => setShowRename(false)} />
      )}

      {showRevokeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: 'var(--color-background-primary)', borderRadius: 14, padding: 28, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ fontSize: 32, marginBottom: 12, textAlign: 'center' }}>⚠️</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 8, textAlign: 'center' }}>
              ¿Revocar agente?
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 6, textAlign: 'center' }}>
              El agente <strong>{agent.name}</strong> dejará de poder conectarse al sistema.
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 24, textAlign: 'center' }}>
              ID: <code style={{ background: 'var(--color-background-secondary)', padding: '1px 6px', borderRadius: 4 }}>{agent.id}</code>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => setShowRevokeModal(false)}
                style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Cancelar
              </button>
              <button
                onClick={handleRevoke}
                disabled={revoking}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: revoking ? '#ccc' : '#dc3545', color: '#fff', cursor: revoking ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                {revoking ? 'Revocando…' : '🚫 Sí, revocar agente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main AgentsModule ──────────────────────────────────────────────────────

export function AgentsModule() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [qAgents, setQAgents] = useState('');

  const loadAgents = useCallback(async () => {
    try {
      const list = await agentsApi.list();
      setAgents(list);
      // Update selected if still present
      if (selectedAgent) {
        const updated = list.find(a => a.id === selectedAgent.id);
        if (updated) setSelectedAgent(updated);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedAgent?.id]);

  useEffect(() => { loadAgents(); }, []);

  // Auto-refresh every 15s
  useEffect(() => {
    const t = setInterval(loadAgents, 15000);
    return () => clearInterval(t);
  }, [loadAgents]);

  const handleRename = async (name: string) => {
    if (!selectedAgent) return;
    const updated = await agentsApi.update(selectedAgent.id, { name });
    setAgents(prev => prev.map(a => a.id === updated.id ? updated : a));
    setSelectedAgent(updated);
  };

  const handleRevoke = async () => {
    if (!selectedAgent) return;
    await agentsApi.delete(selectedAgent.id);
    setAgents(prev => prev.filter(a => a.id !== selectedAgent.id));
    setSelectedAgent(null);
  };

  // Filtered list
  const filteredAgents = qAgents
    ? agents.filter(a => a.name.toLowerCase().includes(qAgents.toLowerCase()) || a.platform.toLowerCase().includes(qAgents.toLowerCase()))
    : agents;

  // Summary counts — usa resolveStatus para homologar con listado y detalle
  const online  = agents.filter(a => resolveStatus(a) === 'online').length;
  const busy    = agents.filter(a => resolveStatus(a) === 'busy').length;
  const offline = agents.filter(a => ['offline', 'error'].includes(resolveStatus(a))).length;

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', overflow: 'hidden', background: 'var(--color-background-primary)' }}>

      {/* LEFT: Agents list */}
      <div style={{ width: 300, borderRight: '1px solid var(--color-border-tertiary)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        {/* Header */}
        <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid var(--color-border-tertiary)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Agentes
            </span>
            <button onClick={loadAgents}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--color-text-tertiary)', padding: 2 }}
              title="Actualizar">
              ↺
            </button>
          </div>
          {/* Summary chips */}
          {agents.length > 0 && (
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                { label: `${online} online`,      color: '#0F6E56', bg: '#E1F5EE', show: online > 0 },
                { label: `${busy} ejecutando`,    color: '#185FA5', bg: '#E6F1FB', show: busy > 0 },
                { label: `${offline} offline`,    color: '#5F5E5A', bg: '#F1EFE8', show: offline > 0 },
              ].filter(c => c.show).map(c => (
                <span key={c.label} style={{ padding: '2px 8px', borderRadius: 6, background: c.bg, color: c.color, fontSize: 10, fontWeight: 600 }}>
                  {c.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* List */}
        <div style={{ padding: '0 8px 6px' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: 'var(--color-text-tertiary)', pointerEvents: 'none' }}>🔍</span>
            <input
              value={qAgents}
              onChange={e => setQAgents(e.target.value)}
              placeholder="Buscar agente..."
              style={{ width: '100%', padding: '5px 24px 5px 24px', fontSize: 11, borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box' }}
            />
            {qAgents && <button onClick={() => setQAgents('')} style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--color-text-tertiary)', padding: 0 }}>✕</button>}
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 10 }}>
          {loading ? <Spinner />
          : filteredAgents.length === 0
            ? <EmptyState
                icon="◉"
                text="Sin agentes registrados"
                sub="Los agentes se registran automáticamente al iniciarse. Revisa la documentación para configurar un agente Playwright."
              />
            : filteredAgents.map(agent => (
              <AgentItem
                key={agent.id}
                agent={agent}
                selected={selectedAgent?.id === agent.id}
                onClick={() => setSelectedAgent(agent)}
              />
            ))}
        </div>
      </div>

      {/* RIGHT: Detail panel */}
      <div style={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
        {!selectedAgent ? (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-tertiary)', gap: 14, padding: 40 }}>
            <span style={{ fontSize: 52 }}>◉</span>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                Selecciona un agente
              </div>
              <div style={{ fontSize: 13, maxWidth: 380, lineHeight: 1.6 }}>
                Los agentes Playwright se conectan automáticamente al backend mediante pull-polling. Cada agente toma casos de prueba pendientes y reporta los resultados en tiempo real.
              </div>
            </div>
            <div style={{ marginTop: 10, padding: '14px 18px', borderRadius: 12, background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)', fontSize: 12, color: 'var(--color-text-secondary)', lineHeight: 1.7, maxWidth: 420 }}>
              <strong>Para registrar un agente:</strong><br />
              <code style={{ fontSize: 11, color: '#3B8BD4' }}>docker compose --profile agent up -d</code>
            </div>
          </div>
        ) : (
          <AgentDetailPanel
            agent={selectedAgent}
            onRename={handleRename}
            onRevoke={handleRevoke}
            onClose={() => setSelectedAgent(null)}
          />
        )}
      </div>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes slideProgress { 0%{transform:translateX(-100%)} 100%{transform:translateX(200%)} }
      `}</style>
    </div>
  );
}